import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const debugLog = (...args) => {
  if (import.meta.env.DEV) {
    console.log(...args)
  }
}

export const useCurrencyStore = defineStore('currency', () => {
  // Default fallback rates (as of Jan 2026)
  const DEFAULT_RATES = {
    AUD: 1.0,
    USD: 0.65,
    EUR: 0.61,
    GBP: 0.52,
    CAD: 0.89,
    JPY: 98.20,
    CNY: 4.68,
    COP: 2650.0,
    MXN: 11.40,
    BRL: 3.25,
    CLP: 610.0,
    ARS: 680.0
  }

  // 1 hour in milliseconds
  const CACHE_DURATION = 60 * 60 * 1000

  const selectedCurrency = ref('AUD')
  const exchangeRates = ref({ ...DEFAULT_RATES })
  const lastUpdated = ref(null)
  const isLoading = ref(false)
  const error = ref(null)

  const needsUpdate = computed(() => {
    if (!lastUpdated.value) return true
    return (Date.now() - lastUpdated.value) > CACHE_DURATION
  })

  function loadCachedRates() {
    try {
      const cachedRates = localStorage.getItem('exchange_rates')
      const cachedTimestamp = localStorage.getItem('exchange_rates_updated')

      if (cachedRates) {
        const parsedRates = JSON.parse(cachedRates)

        // Basic validation of cached rates
        if (parsedRates && typeof parsedRates === 'object' && 'AUD' in parsedRates) {
          exchangeRates.value = {
            ...DEFAULT_RATES, // Use defaults as base
            ...parsedRates    // Override with any valid cached values
          }
          lastUpdated.value = cachedTimestamp ? parseInt(cachedTimestamp) : null
          debugLog('[Currency] Loaded valid cached exchange rates')
          return true
        }
      }

      debugLog('[Currency] No valid cached rates found, will use defaults')
      return false

    } catch (e) {
      console.warn('[Currency] Failed to load cached rates:', e)
      return false
    }
  }

  /**
   * Fetch latest exchange rates from API with retry logic
   * Uses exchangerate.host as a free, reliable source
   * Falls back to cached rates if API fails
   */
  async function fetchRates(maxRetries = 2) {
    // Skip if recently updated
    if (!needsUpdate.value && !error.value) {
      debugLog('[Currency] Using cached exchange rates')
      return
    }

    isLoading.value = true
    error.value = null
    let retryCount = 0

    while (retryCount <= maxRetries) {
      try {
        debugLog(`[Currency] Fetching exchange rates (attempt ${retryCount + 1}/${maxRetries + 1})`)

        // Using open.er-api.com as a more reliable free source
        const response = await fetch('https://open.er-api.com/v6/latest/AUD')

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        // Check for valid response with rates
        if (data && data.rates) {
          const rates = data.rates || {}

          // Validate and update rates
          const updatedRates = { AUD: 1.0 }

          // Dynamically map all available rates that we support
          Object.keys(DEFAULT_RATES).forEach(code => {
            if (rates[code]) {
              updatedRates[code] = parseFloat(rates[code])
            } else {
              updatedRates[code] = DEFAULT_RATES[code]
            }
          })

          exchangeRates.value = updatedRates

          lastUpdated.value = Date.now()

          // Cache rates and timestamp in localStorage
          try {
            localStorage.setItem('exchange_rates', JSON.stringify(exchangeRates.value))
            localStorage.setItem('exchange_rates_updated', lastUpdated.value.toString())
            debugLog('[Currency] Exchange rates cached successfully')
          } catch (e) {
            console.warn('[Currency] Failed to cache exchange rates:', e)
          }

          debugLog('[Currency] Exchange rates updated successfully')
          return // Success, exit the retry loop
        } else {
          throw new Error('Invalid API response format')
        }
      } catch (err) {
        retryCount++

        if (retryCount > maxRetries) {
          console.warn(`[Currency] All ${maxRetries + 1} attempts failed, using fallback rates`)
          // Load cached rates or use defaults
          if (!loadCachedRates()) {
            console.warn('[Currency] Using default fallback rates')
            exchangeRates.value = { ...DEFAULT_RATES }
          }

          error.value = `Failed to fetch rates: ${err.message}. Using ${lastUpdated.value ? 'cached' : 'default'} rates.`
        } else {
          // Wait before retrying (exponential backoff)
          const delay = 1000 * Math.pow(2, retryCount)
          console.warn(`[Currency] Attempt ${retryCount} failed, retrying in ${delay}ms...`)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      } finally {
        isLoading.value = false
      }
    }
  }

  function convertFromAUD(amountInAUD, targetCurrency = null) {
    const currency = targetCurrency || selectedCurrency.value
    const rate = exchangeRates.value[currency] || 1.0
    return amountInAUD * rate
  }

  function convertToAUD(amount, sourceCurrency) {
    const rate = exchangeRates.value[sourceCurrency] || 1.0
    return amount / rate
  }

  function formatCurrency(amount, currency = null, showCode = true) {
    const code = currency || selectedCurrency.value
    const formattedAmount = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)

    return showCode ? `${formattedAmount} ${code}` : formattedAmount
  }

  function getConvertedPrice(audPrice, showCode = true) {
    if (!audPrice && audPrice !== 0) return 'N/A'
    const converted = convertFromAUD(audPrice)
    return formatCurrency(converted, selectedCurrency.value, showCode)
  }

  function setCurrency(currency) {
    if (exchangeRates.value[currency]) {
      selectedCurrency.value = currency

      try {
        localStorage.setItem('currency', currency)
        debugLog(`[Currency] Currency changed to ${currency}`)
      } catch (e) {
        console.warn('[Currency] Failed to save currency preference:', e)
      }

      // Dispatch event for components not using the store
      window.dispatchEvent(new CustomEvent('currency-changed', { detail: currency }))
    } else {
      console.warn(`[Currency] Currency ${currency} not supported`)
    }
  }

  async function initialize(authStore = null) {
    debugLog('[Currency] Initializing currency store...')

    // Set default rates immediately to ensure we always have some rates
    exchangeRates.value = { ...DEFAULT_RATES }

    // Load detected country for fallback
    const { getUserCountry, getCurrencyForCountry } = await import('@/services/utils/userLocation')
    const countryCode = getUserCountry(authStore)

    // Determine target currency (Profile > LocalStorage > Geo-detected > Default)
    const targetCurrency = getCurrencyForCountry(countryCode, authStore)

    if (targetCurrency && (targetCurrency in exchangeRates.value)) {
      selectedCurrency.value = targetCurrency
      debugLog(`[Currency] Set currency to ${targetCurrency} from ${authStore?.isAuthenticated ? 'profile' : 'local/geo'}`)
    }

    // Try to load cached rates first
    const hasCachedRates = loadCachedRates()

    // Always try to fetch fresh rates in background if needed
    if (needsUpdate.value) {
      debugLog('[Currency] Rates need update, fetching fresh rates...')
      fetchRates().catch(err => {
        console.error('[Currency] Failed to fetch rates:', err)
      })
    } else if (hasCachedRates) {
      debugLog('[Currency] Using cached rates (still fresh)')
    } else {
      console.warn('[Currency] No valid cached rates, using defaults')
      exchangeRates.value = { ...DEFAULT_RATES }
    }

    debugLog('[Currency] Initialization complete')
  }

  return {
    // State
    selectedCurrency,
    exchangeRates,
    lastUpdated,
    isLoading,
    error,

    // Getters
    needsUpdate,

    // Methods
    initialize,
    fetchRates,
    loadCachedRates,
    convertFromAUD,
    convertToAUD,
    formatCurrency,
    getConvertedPrice,
    setCurrency
  }
})
